import React, { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import { Input, CrossIcon, SubLabel, Sub1Button, SecondaryButton } from '@components/atoms';
import { HorizontalTable } from '@components/organisms';
import { REGEX_IP, REGEX_ONLY_NUMBER } from '@components/templates/Fields/MyAccount';
import { FilterEchoButtonStyle } from '@components/organisms/Table/Filters/styles';
import {
  PacsSettingsFieldWrapper,
  ScpAeInfoSection,
  SslSection,
  FieldCloseButton,
  StyledTxtError,
  StyledTxtReady,
  StyledTxtSuccess,
  StyledCopyWrap,
} from './style';
import he from 'he';
import { submitDebounce } from '../../../../utils/lib';

interface SettingFieldEvent {
  onChange?: (e: any, index: number) => void;
  onClose?: (idx: number) => void;
  onEcho?: (index: number, type?: string) => void;
  onCopy?: (index: number) => void;
  settingFieldCount?: number;
  type: string;
  displayOrder: number;
  dataCloud?: PacsCloudSettingProps;
  dataPremise?: PacsPremiseSettingProps;
}

interface PacsPremiseSettingProps {
  originDataStringify: string;
  id: string;
  uid?: string;
  forwardPacsAET: string;
  forwardPacsIpAddress: string;
  forwardPacsPort?: string;
  forwardValid?: string;
  protocol?: string;
}

interface PacsCloudSettingProps extends PacsPremiseSettingProps {
  aet: string;
  ipAddress: string;
  port?: string;
  valid?: string;
}

type PacsSettingFieldProps = SettingFieldEvent;

const PacsSettingField = ({
  displayOrder,
  dataCloud,
  dataPremise,
  settingFieldCount = 0,
  onChange = (e: any, index: number) => {
    return;
  },
  onEcho = (index: number, type?: string) => {
    return;
  },
  onCopy = (index: number) => {
    return;
  },
  onClose,
  type = 'CLOUD',
}: PacsSettingFieldProps) => {
  /**
   * 타입에 맞는 데이터 에서 protocol 정보 가져오기
   */
  const getProtocol = () => {
    if (type === 'CLOUD' && dataCloud) return dataCloud.protocol;
    if (type !== 'CLOUD' && dataPremise) return dataPremise.protocol;
  };

  /**
   * 타입에 맞는 데이터 에서 id 정보 가져오기
   */
  const getID = () => {
    if (type === 'CLOUD' && dataCloud) return dataCloud.id;
    if (type !== 'CLOUD' && dataPremise) return dataPremise.id;
  };

  /**
   * 타입에 맞는 데이터 에서 valid 정보 가져오기
   */
  const getValid = () => {
    if (type === 'CLOUD' && dataCloud) return dataCloud.valid;
    if (type !== 'CLOUD' && dataPremise) return dataPremise.forwardValid;
  };

  /**
   * 필수 마크 반환
   * @param isForward
   */
  const hasRequiredMark = (isForward: boolean) => {
    if (type === 'CLOUD') {
      return !isForward ? '*' : '';
    } else {
      return isForward ? '*' : '';
    }
  };

  /**
   * To/From Server IP tag 생성 반환
   */
  const makeToFromIPTag = (data: any, sendType: string) => {
    const isForward = sendType === 'FORWARD';
    const _data = {
      ip: isForward ? data.forwardPacsIpAddress : data.ipAddress,
      valid: isForward ? data.forwardValid : data.valid,
    };

    return (
      <>
        <th>
          {isForward ? 'To' : 'From'} Server IP Address{hasRequiredMark(isForward)}
        </th>
        <td>
          <Input
            type="text"
            name={isForward ? 'forwardPacsIpAddress' : 'ipAddress'}
            value={_data.ip || ''}
            disabled={_data.valid === 'process'}
            onChange={(e) => inputValid('IP', e)}
          />
        </td>
      </>
    );
  };

  /**
   * To/From Server AE Title tag 생성 반환
   */
  const makeToFromAETitleTag = (data: any, sendType: string) => {
    const isForward = sendType === 'FORWARD';

    const _data = {
      aet: isForward ? data.forwardPacsAET : data.aet,
      valid: isForward ? data.forwardValid : data.valid,
    };

    return (
      <>
        <th>{isForward ? 'To' : 'From'} Server AE Title</th>
        <td>
          <Input
            type="text"
            name={isForward ? 'forwardPacsAET' : 'aet'}
            value={he.decode(_data.aet)}
            disabled={_data.valid === 'process'}
            onChange={(e) => onChange(e, displayOrder)}
          />
        </td>
      </>
    );
  };

  /**
   * To/From Server Port tag 생성 반환
   */
  const makeToFromPortTag = (data: any, sendType: string) => {
    const isForward = sendType === 'FORWARD';
    const _data = {
      port: isForward ? data.forwardPacsPort : data.port,
      valid: isForward ? data.forwardValid : data.valid,
    };

    return (
      <>
        <th>
          {sendType === 'FORWARD' ? 'To' : 'From'} Server Port Number{hasRequiredMark(isForward)}
        </th>
        <td>
          <Input
            type="text"
            name={isForward ? 'forwardPacsPort' : 'port'}
            value={_data.port || ''}
            disabled={_data.valid === 'process'}
            onChange={(e) => inputValid('PORT', e)}
          />
        </td>
      </>
    );
  };

  /**
   * To/From Server Echo tag 생성 반환
   */
  const makeToFromEchoTag = (data: any, sendType: string) => {
    const isForward = sendType === 'FORWARD';
    const _data = {
      protocol: data.protocol,
      port: isForward ? data.forwardPacsPort : data.port,
      ip: isForward ? data.forwardPacsIpAddress : data.ipAddress,
      aet: isForward ? data.forwardPacsAET : data.aet,
      valid: isForward ? data.forwardValid : data.valid,
    };
    const _disableCondition = isForward
      ? !_data.protocol || !_data.port || !_data.ip || !_data.aet
      : !_data.protocol || !_data.port || !_data.ip;
    const _visibleConfition = _data.protocol && _data.port && _data.ip && _data.aet;

    const postEcho = () => {
      onEcho(displayOrder, sendType);
    };

    return (
      <>
        <th>{isForward ? 'To' : 'From'} Server Echo</th>
        <td>
          <div className={'echo-status'}>
            <SecondaryButton
              name={isForward ? 'forwardValid' : 'valid'}
              disabled={_disableCondition || _data.valid === 'process'}
              styles={FilterEchoButtonStyle}
              onClick={submitDebounce(postEcho)}
            >
              Echo
            </SecondaryButton>
            {_visibleConfition && _data.valid === 'success' && (
              <StyledTxtSuccess className={'echo-success'}>
                <span>Success!</span>
              </StyledTxtSuccess>
            )}
            {_visibleConfition && _data.valid === 'ready' && (
              <StyledTxtReady className={'echo-success'}>
                <span>Ready</span>
              </StyledTxtReady>
            )}
            {_visibleConfition && _data.valid === 'failed' && (
              <StyledTxtError className={'echo-success'}>
                <span>Error</span>
              </StyledTxtError>
            )}
            {_visibleConfition && _data.valid === 'process' && (
              <StyledTxtReady className={'echo-success'}>
                <span>Waiting</span>
              </StyledTxtReady>
            )}
          </div>
        </td>
      </>
    );
  };

  /**
   * PACS 리스트 생성 반환
   */
  const makeInputSet = () => {
    if (type === 'CLOUD' && dataCloud) {
      return (
        <>
          <tr>
            {makeToFromIPTag(dataCloud, 'TO')}
            {makeToFromIPTag(dataCloud, 'FORWARD')}
          </tr>
          <tr>
            {makeToFromPortTag(dataCloud, 'TO')}
            {makeToFromPortTag(dataCloud, 'FORWARD')}
          </tr>
          <tr>
            {makeToFromAETitleTag(dataCloud, 'TO')}
            {makeToFromAETitleTag(dataCloud, 'FORWARD')}
          </tr>
          <tr>
            {makeToFromEchoTag(dataCloud, 'TO')}
            {makeToFromEchoTag(dataCloud, 'FORWARD')}
          </tr>
        </>
      );
    } else if (type !== 'CLOUD' && dataPremise) {
      return (
        <>
          <tr>
            {makeToFromIPTag(dataPremise, 'FORWARD')}
            {makeToFromAETitleTag(dataPremise, 'FORWARD')}
          </tr>
          <tr>
            {makeToFromPortTag(dataPremise, 'FORWARD')}
            {makeToFromEchoTag(dataPremise, 'FORWARD')}
          </tr>
        </>
      );
    }
  };

  /**
   * input valid
   * @param e
   */
  const inputValid = (type: string, e: any) => {
    let regex = REGEX_IP;
    if (type === 'PORT') regex = REGEX_ONLY_NUMBER;
    e.target.value = e.target.value.replace(regex, '') || '';
    onChange(e, displayOrder);
  };

  const activeData = () => {
    if (type === 'CLOUD' && dataCloud) {
      return dataCloud.ipAddress && dataCloud.port;
    } else {
      return dataPremise && dataPremise.forwardPacsIpAddress && dataPremise.forwardPacsPort;
    }
  };

  return (
    <PacsSettingsFieldWrapper type={type} className={activeData() && 'active'}>
      <ScpAeInfoSection>
        <label>Server(SCP AE) Information {displayOrder + 1}</label>
        <HorizontalTable>
          <tbody>{makeInputSet()}</tbody>
        </HorizontalTable>
        <StyledCopyWrap>
          <Sub1Button name={'copyList'} styles={FilterEchoButtonStyle} onClick={(e) => onCopy(displayOrder)}>
            COPY
          </Sub1Button>
        </StyledCopyWrap>
      </ScpAeInfoSection>
      <SslSection>
        <label>SSL Protocol</label>
        <Input
          type={'radio'}
          name={`protocol_${getID()}`}
          value={'NONE'}
          disabled={getValid() === 'process'}
          checked={getProtocol() === 'NONE'}
          onChange={(e) => onChange(e, displayOrder)}
        >
          None
        </Input>
        <Input
          type={'radio'}
          name={`protocol_${getID()}`}
          value={'TLS1'}
          disabled={getValid() === 'process'}
          checked={getProtocol() === 'TLS1'}
          onChange={(e) => onChange(e, displayOrder)}
        >
          TLS 1.0
        </Input>
        <Input
          type={'radio'}
          name={`protocol_${getID()}`}
          value={'TLS11'}
          disabled={getValid() === 'process'}
          checked={getProtocol() === 'TLS11'}
          onChange={(e) => onChange(e, displayOrder)}
        >
          TLS 1.1
        </Input>
        <Input
          type={'radio'}
          name={`protocol_${getID()}`}
          value={'TLS12'}
          disabled={getValid() === 'process'}
          checked={getProtocol() === 'TLS12'}
          onChange={(e) => onChange(e, displayOrder)}
        >
          TLS 1.2
        </Input>
      </SslSection>
      <FieldCloseButton
        onClick={() => {
          if (onClose) {
            onClose(displayOrder);
          }
        }}
      />
    </PacsSettingsFieldWrapper>
  );
};

export default PacsSettingField;
